import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import { parseEmailContent } from '../../utils/emailParser';

const EmailLogsView = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1
  });
  const [filters, setFilters] = useState({
    email_type: '',
    recipient: '',
    entity_type: '',
    start_date: '',
    end_date: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    logId: null
  });

  // Fetch email logs
  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        },
        params: {
          page,
          limit: pagination.limit,
          ...filters
        }
      };
      
      const response = await axios.get('/api/email/logs', config);
      
      setLogs(response.data.logs);
      setPagination({
        ...pagination,
        page: response.data.pagination.page,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      });
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch log details
  const fetchLogDetails = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get(`/api/email/logs/${id}`, config);
      setSelectedLog(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching log details:', error);
    }
  };

  // Delete log
  const deleteLog = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/email/logs/${id}`, config);
      
      // Refresh logs
      fetchLogs(pagination.page);
      
      // Close details if open
      if (selectedLog && selectedLog.id === id) {
        setShowDetails(false);
        setSelectedLog(null);
      }
      
      // Close delete modal
      setDeleteModal({
        isOpen: false,
        logId: null
      });
    } catch (error) {
      console.error('Error deleting log:', error);
    }
  };
  
  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    setDeleteModal({
      isOpen: true,
      logId: id
    });
  };
  
  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      logId: null
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchLogs(newPage);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Handle filter submit
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      email_type: '',
      recipient: '',
      entity_type: '',
      start_date: '',
      end_date: ''
    });
    
    // Fetch with reset filters
    setTimeout(() => fetchLogs(1), 0);
  };

  // Load logs on component mount
  useEffect(() => {
    fetchLogs();
  }, []);
  
  // Add escape key event listener
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showDetails) {
        setShowDetails(false);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showDetails]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get email type badge class
  const getTypeBadge = (type) => {
    switch (type) {
      case 'course_reminder_tutor':
        return 'bg-blue-100 text-blue-800';
      case 'course_reminder_student':
        return 'bg-purple-100 text-purple-800';
      case 'manual':
        return 'bg-indigo-100 text-indigo-800';
      case 'campaign':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get friendly name for email type
  const getTypeName = (type) => {
    switch (type) {
      case 'course_reminder_tutor':
        return 'Course Reminder (Tutor)';
      case 'course_reminder_student':
        return 'Course Reminder (Student)';
      case 'manual':
        return 'Manual Email';
      case 'campaign':
        return 'Campaign Email';
      default:
        return type;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">System Email Logs</h1>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <h2 className="text-lg font-medium mb-3">Filters</h2>
        <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Type</label>
            <select
              name="email_type"
              value={filters.email_type}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="course_reminder_tutor">Course Reminder (Tutor)</option>
              <option value="course_reminder_student">Course Reminder (Student)</option>
              <option value="manual">Manual Email</option>
              <option value="campaign">Campaign Email</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
            <input
              type="text"
              name="recipient"
              value={filters.recipient}
              onChange={handleFilterChange}
              placeholder="Email address"
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
            <select
              name="entity_type"
              value={filters.entity_type}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Entities</option>
              <option value="calendar_event">Calendar Event</option>
              <option value="lead">Lead</option>
              <option value="deal">Deal</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div className="flex items-end space-x-3">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              Apply Filters
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
            >
              Reset
            </button>
          </div>
        </form>
      </div>
      
      {/* Email Logs Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="loader"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No email logs found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sent At
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => fetchLogDetails(log.id)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadge(log.email_type)}`}>
                        {getTypeName(log.email_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.recipient}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {log.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(log.sent_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(log.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  pagination.page === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  pagination.page === pagination.totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{logs.length ? (pagination.page - 1) * pagination.limit + 1 : 0}</span> to{' '}
                  <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                  <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      pagination.page === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {[...Array(pagination.totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pagination.page === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      pagination.page === pagination.totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Email Details Modal */}
      {showDetails && selectedLog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowDetails(false)}></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Email Details
                    </h3>
                    
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Email Type</h4>
                          <p className="mt-1">{getTypeName(selectedLog.email_type)}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Status</h4>
                          <p className="mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedLog.status)}`}>
                              {selectedLog.status}
                            </span>
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Recipient</h4>
                          <p className="mt-1">{selectedLog.recipient}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Sent At</h4>
                          <p className="mt-1">{formatDate(selectedLog.sent_at)}</p>
                        </div>
                        
                        <div className="sm:col-span-2">
                          <h4 className="text-sm font-medium text-gray-500">Subject</h4>
                          <p className="mt-1">{selectedLog.subject}</p>
                        </div>
                        
                        {selectedLog.related_entity_type && (
                          <div className="sm:col-span-2">
                            <h4 className="text-sm font-medium text-gray-500">Related To</h4>
                            <p className="mt-1">
                              {selectedLog.related_entity_type === 'calendar_event' && (
                                <Link 
                                  to={`/calendar`} 
                                  className="text-blue-600 hover:text-blue-800"
                                  onClick={() => setShowDetails(false)}
                                >
                                  Calendar Event (ID: {selectedLog.related_entity_id})
                                </Link>
                              )}
                              
                              {selectedLog.related_entity_type === 'lead' && (
                                <Link 
                                  to={`/leads/${selectedLog.related_entity_id}`} 
                                  className="text-blue-600 hover:text-blue-800"
                                  onClick={() => setShowDetails(false)}
                                >
                                  Lead (ID: {selectedLog.related_entity_id})
                                </Link>
                              )}
                              
                              {selectedLog.related_entity_type === 'deal' && (
                                <Link 
                                  to={`/deals`} 
                                  className="text-blue-600 hover:text-blue-800"
                                  onClick={() => setShowDetails(false)}
                                >
                                  Deal (ID: {selectedLog.related_entity_id})
                                </Link>
                              )}
                            </p>
                          </div>
                        )}
                        
                        {selectedLog.error_message && (
                          <div className="sm:col-span-2">
                            <h4 className="text-sm font-medium text-gray-500">Error Message</h4>
                            <p className="mt-1 text-red-600">{selectedLog.error_message}</p>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-500">Email Content</h4>
                        <div className="mt-2 p-4 border border-gray-300 rounded-md bg-gray-50 max-h-96 overflow-y-auto">
                          {selectedLog.message_body ? (
                            (() => {
                              const { content, isHtml } = parseEmailContent(selectedLog.message_body);
                              
                              if (!content) {
                                return <p className="text-sm text-gray-800">No content available</p>;
                              }
                              
                              if (isHtml) {
                                return (
                                  <div 
                                    className="prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: content }} 
                                  />
                                );
                              } else {
                                return (
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                    {content}
                                  </p>
                                );
                              }
                            })()
                          ) : (
                            <p className="text-sm text-gray-800">No content available</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => openDeleteModal(selectedLog.id)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={() => deleteLog(deleteModal.logId)}
        title="Delete Email Log"
        message="Are you sure you want to delete this email log? This action cannot be undone."
      />
    </div>
  );
};

export default EmailLogsView;